/**
 * 全局 Toast Context
 * 用于在应用任意位置显示 Toast 消息
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { ToastContainer, ToastProps, ToastVariant } from '@/shared/components/ui/toast'

interface ToastOptions {
  title?: string
  variant?: ToastVariant
  duration?: number
}

interface ToastContextValue {
  toasts: ToastProps[]  // 暴露 toast 列表供需要手动渲染的页面使用
  showToast: (description: string, options?: ToastOptions) => string
  showSuccess: (description: string, title?: string) => string
  showError: (description: string, title?: string) => string
  showWarning: (description: string, title?: string) => string
  showInfo: (description: string, title?: string) => string
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastProps[]>([])

  const showToast = useCallback((description: string, options?: ToastOptions) => {
    const id = `toast-${Date.now()}-${Math.random()}`
    const toast: ToastProps = {
      id,
      description,
      title: options?.title,
      variant: options?.variant || 'default',
      duration: options?.duration ?? 5000,
    }

    setToasts((prev) => [...prev, toast])
    return id
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const showSuccess = useCallback((description: string, title?: string) => {
    return showToast(description, { title, variant: 'success' })
  }, [showToast])

  const showError = useCallback((description: string, title?: string) => {
    return showToast(description, { title, variant: 'error', duration: 7000 })
  }, [showToast])

  const showWarning = useCallback((description: string, title?: string) => {
    return showToast(description, { title, variant: 'warning' })
  }, [showToast])

  const showInfo = useCallback((description: string, title?: string) => {
    return showToast(description, { title, variant: 'info' })
  }, [showToast])

  return (
    <ToastContext.Provider
      value={{
        toasts,
        showToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        removeToast,
      }}
    >
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  
  // 如果在 ToastProvider 外使用，返回空操作（避免崩溃）
  if (!context) {
    console.warn('useToast must be used within a ToastProvider')
    return {
      toasts: [],
      showToast: () => '',
      showSuccess: () => '',
      showError: () => '',
      showWarning: () => '',
      showInfo: () => '',
      removeToast: () => {},
    }
  }
  
  return context
}
